import type { MouseEvent } from "react";

import CloseXIcon from "@svg/close_x.svg";

const REGION = "Action Sheet";

export interface ActionSheetButton {
  imageUrl?: string;
  label?: string;
  [key: string]: unknown;
}

type ActionSheetProps = {
  buttons?: ActionSheetButton[];
  title?: string;
  hasExitButton?: boolean;
  onButtonClick: (button: ActionSheetButton) => void;
  onExit?: (region: string) => void;
};

export default function ActionSheet({
  buttons,
  title,
  hasExitButton,
  onButtonClick,
  onExit,
}: ActionSheetProps) {
  const onButtonClicked = (e: MouseEvent, button: ActionSheetButton) => {
    onButtonClick(button);
    e.preventDefault();
  };

  const onExitClicked = (e: MouseEvent) => {
    if (onExit) {
      onExit(REGION);
    }
    e.preventDefault();
  };

  const getButton = (n: number, button: ActionSheetButton) => {
    const style: { backgroundImage?: string } = {};
    if (button.imageUrl) {
      style.backgroundImage = `url(${button.imageUrl})`;
    }

    return (
      <button
        key={`action-btn-${n}`}
        type="button"
        className="action-btn hover type-5 button wide tw:relative tw:w-full"
        aria-label={button.label ? `Shop at ${button.label}` : "Shop online"}
        onClick={e => onButtonClicked(e, button)}
      >
        <div
          className="btn-image tw:absolute tw:top-[18px] tw:right-[20px] tw:bottom-[18px] tw:left-[20px] tw:bg-center tw:bg-contain tw:bg-no-repeat"
          style={style}
        ></div>
      </button>
    );
  };

  const getButtons = () => {
    const ret = [];
    for (let n = 0, max = buttons ? buttons.length : 0; n < max; n++) {
      if (n > 0) {
        ret.push(
          <div
            key={`action-btn-sep${n}`}
            className="separator tw:h-[1px] tw:w-full tw:bg-[rgba(200,200,200,0.5)]"
          />,
        );
      }
      ret.push(getButton(n, (buttons as ActionSheetButton[])[n]));
    }

    return (
      <div className="action-buttons tw:shrink tw:grow tw:[.landscape_&]:overflow-auto tw:[.portrait_&]:[box-shadow:0_0_5px_2px_#e1e1e1] tw:[.portrait_&]:[margin:0_20px_20px_20px]">
        {ret}
      </div>
    );
  };

  const getTitle = () => {
    const exit = hasExitButton ? (
      <button
        className="exit-shop-window hover type-3 button square tw:right-[6px]"
        aria-label="Close"
        onClick={e => onExitClicked(e)}
      >
        <CloseXIcon className="centered icon-close-x-d" />
      </button>
    ) : null;

    if (title) {
      return (
        <div className="title tw:relative tw:text-center tw:[padding:20px_0_20px_0] tw:[.portrait_&]:mt-[6px] tw:[.portrait_&]:mb-[10px]">
          {exit}
          {title}
        </div>
      );
    }
  };

  return (
    <div className="action-sheet tw:flex tw:flex-col tw:bg-[#fff] tw:[.landscape_&]:absolute tw:[.landscape_&]:right-[10px] tw:[.landscape_&]:bottom-[0px] tw:[.landscape_&]:left-[10px] tw:[.landscape_&]:max-h-full tw:[.landscape_&]:[box-shadow:0_0_5px_2px_#e1e1e1] tw:[.landscape_&]:[margin:10px_20px_0_20px] tw:[.portrait_&]:overflow-auto">
      {getTitle()}
      <div className="separator title-separator tw:h-[1px] tw:w-full tw:bg-[rgba(200,200,200,0.5)]" />
      {getButtons()}
    </div>
  );
}
